/**
 * WhatsApp Settings page.
 *
 * - Working hours (no late-night messages)
 * - Auto-reminder for overdue invoices (cron — not yet wired)
 * - Daily summary recipients
 * - Per-doc-type "auto-send on Post" toggles
 * - Disconnect button
 *
 * All settings persisted to companies/{id}/settings/whatsapp (merged on existing doc).
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { FiSave, FiInfo, FiClock, FiBell, FiLogOut } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { colors } from '../../theme/colors';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import usePagePermissions from '../../hooks/usePagePermissions';
import { callWhatsAppService } from '../../utils/whatsappHelper';
import { DEFAULT_TEMPLATES } from '../../utils/whatsappTemplates';

const DEFAULT_SETTINGS = {
  workingHours: { enabled: false, start: '09:00', end: '21:00' },
  autoReminders: { enabled: false, daysOverdue: 7, sendTime: '09:00' },
  dailySummary: { enabled: false, recipients: '', sendTime: '21:00' },
  autoSendOnPost: {}    // { 'SI': true, 'CR': false, ... }
};

const POST_TRIGGERS = DEFAULT_TEMPLATES
  .filter(t => t.tranType && t.tranType !== 'PAY' && t.tranType !== 'FNF')
  .map(t => ({ tranType: t.tranType, label: t.label }));

const WhatsAppSettings = () => {
  const { activeCompany, currentUser } = useAuth();
  const { canEdit } = usePagePermissions('whatsapp', 'settings');
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [status, setStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState(null);

  const load = useCallback(async () => {
    if (!activeCompany?.id) return;
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'companies', activeCompany.id, 'settings', 'whatsapp'));
      if (snap.exists()) {
        const d = snap.data();
        setStatus(d);
        setSettings({
          workingHours: { ...DEFAULT_SETTINGS.workingHours, ...(d.workingHours || {}) },
          autoReminders: { ...DEFAULT_SETTINGS.autoReminders, ...(d.autoReminders || {}) },
          dailySummary: { ...DEFAULT_SETTINGS.dailySummary, ...(d.dailySummary || {}) },
          autoSendOnPost: d.autoSendOnPost || {}
        });
      }
    } catch (e) {
      setAlert({ type: 'error', message: e.message });
    } finally { setLoading(false); }
  }, [activeCompany?.id]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      await setDoc(doc(db, 'companies', activeCompany.id, 'settings', 'whatsapp'), {
        ...settings,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.uid || null
      }, { merge: true });
      setAlert({ type: 'success', message: 'Settings saved.' });
    } catch (e) {
      setAlert({ type: 'error', message: e.message });
    } finally { setSaving(false); }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect WhatsApp? You will need to scan the QR again.')) return;
    try {
      await callWhatsAppService('disconnect', 'POST', { companyId: activeCompany.id });
      setAlert({ type: 'success', message: 'WhatsApp disconnected.' });
      await load();
    } catch (e) {
      setAlert({ type: 'error', message: e.message });
    }
  };

  return (
    <PageLayout pageTitle="WhatsApp Settings">
      <Container>
        <Header>
          <Title><FaWhatsapp /> WhatsApp Settings</Title>
          <Subtitle>Configure auto-reminders, working hours, and per-document auto-send behavior.</Subtitle>
        </Header>

        {alert && <Alert $type={alert.type}><FiInfo /> {alert.message}</Alert>}

        <ConnectionPanel>
          <Status>
            <StatusDot $on={!!status.connected} />
            <div>
              <StatusTitle>{status.connected ? 'Connected' : 'Not Connected'}</StatusTitle>
              {status.phoneNumber && <StatusDetail>Linked as <b>{status.phoneNumber}</b></StatusDetail>}
            </div>
          </Status>
          {status.connected && (
            <DangerBtn onClick={handleDisconnect}><FiLogOut /> Disconnect</DangerBtn>
          )}
        </ConnectionPanel>

        {loading ? <Loading>Loading…</Loading> : (
          <>
            <Section>
              <SectionTitle><FiClock /> Working Hours</SectionTitle>
              <SectionDesc>If enabled, scheduled/auto-sends only fire during these hours. Manual sends always work.</SectionDesc>
              <Toggle>
                <input type="checkbox" checked={settings.workingHours.enabled} onChange={e => setSettings({...settings, workingHours: {...settings.workingHours, enabled: e.target.checked}})} disabled={!canEdit} />
                <span>Restrict to working hours</span>
              </Toggle>
              <Row>
                <Field>
                  <Label>Start</Label>
                  <Input type="time" value={settings.workingHours.start} onChange={e => setSettings({...settings, workingHours: {...settings.workingHours, start: e.target.value}})} disabled={!canEdit || !settings.workingHours.enabled} />
                </Field>
                <Field>
                  <Label>End</Label>
                  <Input type="time" value={settings.workingHours.end} onChange={e => setSettings({...settings, workingHours: {...settings.workingHours, end: e.target.value}})} disabled={!canEdit || !settings.workingHours.enabled} />
                </Field>
              </Row>
            </Section>

            <Section>
              <SectionTitle><FiBell /> Payment Reminders (Auto-Cron)</SectionTitle>
              <SectionDesc>Daily reminder messages to customers with overdue invoices. Phase 2B feature — toggle saves the preference.</SectionDesc>
              <Toggle>
                <input type="checkbox" checked={settings.autoReminders.enabled} onChange={e => setSettings({...settings, autoReminders: {...settings.autoReminders, enabled: e.target.checked}})} disabled={!canEdit} />
                <span>Send daily reminder to overdue customers</span>
              </Toggle>
              <Row>
                <Field>
                  <Label>Days overdue threshold</Label>
                  <Input type="number" min="0" value={settings.autoReminders.daysOverdue} onChange={e => setSettings({...settings, autoReminders: {...settings.autoReminders, daysOverdue: Number(e.target.value) || 0}})} disabled={!canEdit} />
                </Field>
                <Field>
                  <Label>Send time</Label>
                  <Input type="time" value={settings.autoReminders.sendTime} onChange={e => setSettings({...settings, autoReminders: {...settings.autoReminders, sendTime: e.target.value}})} disabled={!canEdit} />
                </Field>
              </Row>
            </Section>

            <Section>
              <SectionTitle>📊 Daily Sales Summary</SectionTitle>
              <SectionDesc>End-of-day digest to owner: today's revenue, top customer, receipts, low-stock alerts. Phase 2B feature.</SectionDesc>
              <Toggle>
                <input type="checkbox" checked={settings.dailySummary.enabled} onChange={e => setSettings({...settings, dailySummary: {...settings.dailySummary, enabled: e.target.checked}})} disabled={!canEdit} />
                <span>Send daily summary to owner</span>
              </Toggle>
              <Row>
                <Field $wide>
                  <Label>Recipient phone numbers (comma-separated, with country code)</Label>
                  <Input placeholder="923001234567, 923009876543" value={settings.dailySummary.recipients} onChange={e => setSettings({...settings, dailySummary: {...settings.dailySummary, recipients: e.target.value}})} disabled={!canEdit} />
                </Field>
                <Field>
                  <Label>Send time</Label>
                  <Input type="time" value={settings.dailySummary.sendTime} onChange={e => setSettings({...settings, dailySummary: {...settings.dailySummary, sendTime: e.target.value}})} disabled={!canEdit} />
                </Field>
              </Row>
            </Section>

            <Section>
              <SectionTitle>📤 Auto-Send on Post</SectionTitle>
              <SectionDesc>When enabled, posting one of these documents automatically sends it to the customer/vendor over WhatsApp (no manual click).</SectionDesc>
              <PostTriggers>
                {POST_TRIGGERS.map(t => (
                  <Toggle key={t.tranType}>
                    <input
                      type="checkbox"
                      checked={!!settings.autoSendOnPost[t.tranType]}
                      onChange={e => setSettings({
                        ...settings,
                        autoSendOnPost: { ...settings.autoSendOnPost, [t.tranType]: e.target.checked }
                      })}
                      disabled={!canEdit}
                    />
                    <span>{t.label} <small style={{ color: '#888' }}>({t.tranType})</small></span>
                  </Toggle>
                ))}
              </PostTriggers>
            </Section>

            <SaveRow>
              <SaveBtn onClick={save} disabled={!canEdit || saving}>
                <FiSave /> {saving ? 'Saving…' : 'Save All Settings'}
              </SaveBtn>
            </SaveRow>
          </>
        )}
      </Container>
    </PageLayout>
  );
};

export default WhatsAppSettings;

const Container = styled.div`max-width: 900px; margin: 0 auto; padding: 24px; display: flex; flex-direction: column; gap: 16px;`;
const Header = styled.div``;
const Title = styled.h1`margin: 0; font-size: 24px; font-weight: 700; color: ${colors.text.primary}; display: flex; align-items: center; gap: 10px;`;
const Subtitle = styled.div`color: ${colors.text.secondary}; font-size: 14px; margin-top: 4px;`;
const Alert = styled.div`display: flex; align-items: center; gap: 8px; padding: 10px 14px; border-radius: 8px; font-size: 14px;
  background: ${p => p.$type === 'success' ? '#e8f5e9' : p.$type === 'error' ? '#fdecea' : '#e7f1ff'};
  color: ${p => p.$type === 'success' ? '#1b5e20' : p.$type === 'error' ? '#b00020' : '#0d47a1'};`;
const ConnectionPanel = styled.div`display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; background: #fff; border: 1px solid #eee; border-radius: 10px;`;
const Status = styled.div`display: flex; align-items: center; gap: 12px;`;
const StatusDot = styled.div`width: 14px; height: 14px; border-radius: 50%; background: ${p => p.$on ? '#25D366' : '#bdbdbd'};`;
const StatusTitle = styled.div`font-weight: 700;`;
const StatusDetail = styled.div`font-size: 12px; color: ${colors.text.secondary};`;
const Section = styled.div`background: #fff; border: 1px solid #eee; border-radius: 10px; padding: 18px 20px;`;
const SectionTitle = styled.div`display: flex; align-items: center; gap: 8px; font-size: 16px; font-weight: 700; margin-bottom: 6px;`;
const SectionDesc = styled.div`font-size: 13px; color: ${colors.text.secondary}; margin-bottom: 12px;`;
const Toggle = styled.label`display: flex; align-items: center; gap: 8px; font-size: 14px; cursor: pointer; margin: 8px 0; input { width: 16px; height: 16px; cursor: pointer; }`;
const Row = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-top: 8px;`;
const Field = styled.div`grid-column: ${p => p.$wide ? '1 / -1' : 'auto'};`;
const Label = styled.label`display: block; font-size: 12px; font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase; margin-bottom: 4px;`;
const Input = styled.input`width: 100%; padding: 9px 12px; border: 1px solid #ddd; border-radius: 6px; font-family: inherit; font-size: 14px; &:disabled { opacity: 0.6; }`;
const PostTriggers = styled.div`display: grid; grid-template-columns: 1fr 1fr; gap: 4px 16px;`;
const SaveRow = styled.div`display: flex; justify-content: flex-end;`;
const SaveBtn = styled.button`display: inline-flex; align-items: center; gap: 8px; background: #800020; color: #fff; border: none; padding: 12px 20px; border-radius: 6px; font-weight: 600; cursor: pointer; &:disabled { opacity: 0.6; cursor: not-allowed; }`;
const DangerBtn = styled.button`display: inline-flex; align-items: center; gap: 8px; background: #b00020; color: #fff; border: none; padding: 8px 14px; border-radius: 6px; font-size: 14px; cursor: pointer;`;
const Loading = styled.div`padding: 40px; text-align: center; color: ${colors.text.secondary};`;
